/**
 * Thin, defensive wrapper around the LogRocket SDK.
 *
 * LogRocket is initialised lazily in src/index.js (production browser only, and
 * skipped for bots). This module lets other code emit custom tracked events
 * without importing the SDK directly or caring whether it is active. Every call
 * is a no-op when LogRocket is not running (SSR, non-production, bot UA, load
 * failure), and never throws into the caller.
 */

let logRocketPromise = null;

const isEnabled = () =>
  typeof window !== 'undefined' &&
  !!process.env.REACT_APP_LOGROCKET_APP_ID &&
  process.env.NODE_ENV === 'production';

const getLogRocket = () => {
  if (!isEnabled()) {
    return Promise.resolve(null);
  }
  if (!logRocketPromise) {
    logRocketPromise = import('logrocket')
      .then(({ default: LogRocket }) => LogRocket)
      .catch(() => null);
  }
  return logRocketPromise;
};

/**
 * Emit a custom LogRocket event. Property values must be strings, numbers or
 * booleans — anything else is dropped by LogRocket, so callers should flatten
 * their payload first. Safe to call unconditionally.
 *
 * @param {string} name Event name, e.g. "Checkout Payment Expired Seen"
 * @param {Object} [properties] Flat map of scalar properties
 */
export const trackEvent = (name, properties = {}) => {
  if (!name) return;
  getLogRocket().then(LogRocket => {
    if (LogRocket && typeof LogRocket.track === 'function') {
      try {
        LogRocket.track(name, properties);
      } catch (e) {
        // Never let telemetry break a user flow.
      }
    }
  });
};
